import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  TextInput,
  Textarea,
  Label,
  Tabs,
  Checkbox,
  Table,
} from "flowbite-react";
import { observer } from "mobx-react-lite";
import {
  HiKey,
  HiTemplate,
  HiSave,
  HiChip,
  HiBell,
  HiDuplicate,
  HiInformationCircle,
  HiEye,
  HiEyeOff,
  HiPhotograph,
} from "react-icons/hi";
import { tenantStore } from "../stores/tenantStore";
import { authStore } from "../stores/authStore";
import { toastStore } from "../stores/toastStore";
import APIKeyManager from "../components/APIKeyManager";
import CropSelector from "../components/CropSelector";
import { tenantApi } from "../services/tenantApi";
import type { ImageConfig, CropRegion } from "../types";
import { screenService } from "../services/screenService";

const SettingsPage: React.FC = observer(() => {
  const [registerApiKey, setRegisterApiKey] = useState("");
  const [showRegisterApiKey, setShowRegisterApiKey] = useState(false);
  const [channelAccessToken, setChannelAccessToken] = useState("");
  const [channelSecret, setChannelSecret] = useState("");
  const [showChannelAccessToken, setShowChannelAccessToken] = useState(false);
  const [showChannelSecret, setShowChannelSecret] = useState(false);
  const [messageTemplates, setMessageTemplates] = useState({
    calling:
      "【呼び出し】\nお呼び出しいたします。\n番号: {{number}}\n場所: {{location}}\nお早めにお越しください。",
    registration: "順番が近づいたらお知らせします",
    callingTitle: "",
    registrationTitle: "",
  });
  const [notificationConfig, setNotificationConfig] = useState({
    notifyOnRegistration: true,
    notifyOnCalling: true,
    hideCallNumber: false,
    sendImageWithNotification: false,
    enableTimeRestriction: false,
    startTime: "09:00",
    endTime: "18:00",
    weekdayRestrictions: Array(7)
      .fill(null)
      .map(() => ({
        enabled: false,
        allDay: false,
        startTime: "09:00",
        endTime: "18:00",
      })),
  });
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [imageConfig, setImageConfig] = useState<ImageConfig>({
    enableCrop: false,
    cropRegion: { x: 0, y: 0, width: 0, height: 0 },
  });
  const [latestImageUrl, setLatestImageUrl] = useState<string | null>(null);

  // 現在のドメインから自動的にURLを生成
  const tenantId = authStore.effectiveTenantId;
  const getApiBaseUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL || "http://localhost:3000";
    }
    return `https://${hostname.replace("admin.", "api.")}`;
  };
  const registerEndpointUrl = tenantId
    ? `${getApiBaseUrl()}/v1/${tenantId}/register`
    : "";

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toastStore.showSuccess("クリップボードにコピーしました");
    } catch (err) {
      toastStore.showError("コピーに失敗しました");
    }
  };

  useEffect(() => {
    loadSettings();
  }, [authStore.effectiveTenantId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const tenantId = authStore.effectiveTenantId;
      if (!tenantId) return;

      // Tenant admins can only access their own tenant settings via /v1/admin/tenant
      // Root admins can access any tenant via getTenant, but for consistency use getCurrentTenant for own tenant
      const tenant = await tenantStore.getTenant(tenantId);
      if (tenant?.settings) {
        // LINE設定
        setRegisterApiKey(tenant.settings.apiKeys?.registerApiKey || "");
        setChannelAccessToken(
          tenant.settings.apiKeys?.lineChannelAccessToken || "",
        );
        setChannelSecret(tenant.settings.apiKeys?.lineChannelSecret || "");

        // メッセージテンプレート
        if (tenant.settings.messageTemplates) {
          setMessageTemplates({
            calling: tenant.settings.messageTemplates.calling || "",
            registration:
              tenant.settings.messageTemplates.registration ||
              "順番が近づいたらお知らせします",
            callingTitle: tenant.settings.messageTemplates.callingTitle || "",
            registrationTitle:
              tenant.settings.messageTemplates.registrationTitle || "",
          });
        }

        // 通知設定
        if (tenant.settings.notificationConfig) {
          const defaultWeekdayRestrictions = Array(7)
            .fill(null)
            .map(() => ({
              enabled: false,
              allDay: false,
              startTime: "09:00",
              endTime: "18:00",
            }));

          setNotificationConfig({
            notifyOnRegistration:
              tenant.settings.notificationConfig.notifyOnRegistration ?? true,
            notifyOnCalling:
              tenant.settings.notificationConfig.notifyOnCalling ?? true,
            hideCallNumber:
              tenant.settings.notificationConfig.hideCallNumber || false,
            sendImageWithNotification:
              tenant.settings.notificationConfig.sendImageWithNotification || false,
            enableTimeRestriction:
              tenant.settings.notificationConfig.enableTimeRestriction || false,
            startTime: tenant.settings.notificationConfig.startTime || "09:00",
            endTime: tenant.settings.notificationConfig.endTime || "18:00",
            weekdayRestrictions:
              tenant.settings.notificationConfig.weekdayRestrictions ||
              defaultWeekdayRestrictions,
          });

        }

        // 画像設定
        if (tenant.settings.imageConfig) {
          setImageConfig({
            enableCrop: tenant.settings.imageConfig.enableCrop,
            cropRegion: tenant.settings.imageConfig.cropRegion ?? {
              x: 0,
              y: 0,
              width: 0,
              height: 0,
            },
          });
        }
      }

      // 最新スクリーン画像を取得してサンプル表示
      try {
        const screensRes = await screenService.getScreens({ page: 1, limit: 1 });
        if (screensRes.items && screensRes.items.length > 0 && screensRes.items[0].imageUrl) {
          setLatestImageUrl(screensRes.items[0].imageUrl);
        }
      } catch {
        // 画像取得失敗は無視（プレースホルダーを表示）
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
      toastStore.showError("設定の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLINESettings = async () => {
    setLoading(true);

    try {
      const tenantId = authStore.effectiveTenantId;
      if (!tenantId) throw new Error("テナントIDが取得できません");

      // 差分のみを構築して送信
      const updatedSettings = {
        apiKeys: {
          registerApiKey: registerApiKey,
          lineChannelAccessToken: channelAccessToken,
          lineChannelSecret: channelSecret,
        },
        notificationConfig: {
          notifyOnRegistration: notificationConfig.notifyOnRegistration,
          notifyOnCalling: notificationConfig.notifyOnCalling,
          hideCallNumber: notificationConfig.hideCallNumber,
          sendImageWithNotification: notificationConfig.sendImageWithNotification,
          weekdayRestrictions: notificationConfig.weekdayRestrictions,
        },
      };

      await tenantStore.updateTenantSettings(tenantId, updatedSettings);
      toastStore.showSuccess("LINE設定を保存しました");
    } catch (err) {
      console.error("Failed to save LINE settings:", err);
      toastStore.showError("LINE設定の保存に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplates = async () => {
    setLoading(true);

    try {
      const tenantId = authStore.effectiveTenantId;
      if (!tenantId) throw new Error("テナントIDが取得できません");

      // メッセージテンプレートのみを送信
      const updatedSettings = {
        messageTemplates: messageTemplates,
      };

      await tenantStore.updateTenantSettings(tenantId, updatedSettings);
      toastStore.showSuccess("テンプレートを保存しました");
    } catch (err) {
      console.error("Failed to save templates:", err);
      toastStore.showError("テンプレートの保存に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setLoading(true);

    try {
      const tenantId = authStore.effectiveTenantId;
      if (!tenantId) throw new Error("テナントIDが取得できません");

      // weekdayRestrictions の空の時刻値にデフォルト値を設定
      const normalizedWeekdayRestrictions =
        notificationConfig.weekdayRestrictions.map((restriction) => ({
          enabled: restriction.enabled,
          allDay: restriction.allDay,
          startTime:
            restriction.allDay || !restriction.enabled
              ? restriction.startTime
              : restriction.startTime || "09:00",
          endTime:
            restriction.allDay || !restriction.enabled
              ? restriction.endTime
              : restriction.endTime || "18:00",
        }));

      // 差分のみを構築して送信
      const updatedSettings = {
        notificationConfig: {
          notifyOnRegistration: notificationConfig.notifyOnRegistration,
          notifyOnCalling: notificationConfig.notifyOnCalling,
          hideCallNumber: notificationConfig.hideCallNumber,
          sendImageWithNotification: notificationConfig.sendImageWithNotification,
          weekdayRestrictions: normalizedWeekdayRestrictions, // 曜日別通知時間帯設定（デフォルト値適用済み）
        },
      };

      await tenantStore.updateTenantSettings(tenantId, updatedSettings);
      toastStore.showSuccess("通知設定を保存しました");
    } catch (err) {
      console.error("Failed to save notification settings:", err);
      toastStore.showError("通知設定の保存に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveImageSettings = async () => {
    setLoading(true);
    try {
      const tenantId = authStore.effectiveTenantId;
      if (!tenantId) throw new Error("テナントIDが取得できません");

      const config: ImageConfig = {
        enableCrop: imageConfig.enableCrop,
        cropRegion: imageConfig.enableCrop ? imageConfig.cropRegion : undefined,
      };

      await tenantApi.updateCurrentImageSettings(config);
      await loadSettings();
      toastStore.showSuccess("画像設定を保存しました");
    } catch (err) {
      console.error("Failed to save image settings:", err);
      toastStore.showError("画像設定の保存に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">テナント設定</h1>
        <p className="text-gray-600">LINE連携と通知テンプレートの管理</p>
      </div>

      <Tabs>
        <Tabs.Item active title="LINE設定" icon={HiKey}>
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <HiInformationCircle className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-800">登録エンドポイント</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              外部システムから患者の待受番号を登録するためのAPIエンドポイントです。
            </p>
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 font-sans">エンドポイント</span>
                  <Button
                    size="xs"
                    color="gray"
                    onClick={() => copyToClipboard(registerEndpointUrl + "?callContext={callContext}", "endpoint")}
                  >
                    <HiDuplicate className="w-3 h-3 mr-1" />
                    {copiedField === "endpoint" ? "コピー済" : "コピー"}
                  </Button>
                </div>
                <div className="text-blue-700 break-all">
                  POST {registerEndpointUrl}?callContext=&#123;callContext&#125;
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 font-sans mb-1">ヘッダー</div>
                <div className="text-gray-700">X-API-Key: YOUR_API_KEY</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 font-sans mb-1">リクエストボディ</div>
                <pre className="text-gray-700 whitespace-pre">{`{
  "lineUserId": "LINE_USER_ID",
  "callNumber": "番号"
}`}</pre>
              </div>
              <div>
                <div className="text-xs text-gray-500 font-sans mb-1">callContext の値</div>
                <div className="text-gray-600 space-y-0.5">
                  <div>examination : 診察</div>
                  <div>accounting  : 会計</div>
                  <div>laboratory  : 検査</div>
                  <div>pharmacy    : 薬局</div>
                  <div>other       : その他</div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="mt-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="registerApiKey" value="登録エンドポイント APIキー" />
                <div className="flex gap-2">
                  <TextInput
                    id="registerApiKey"
                    type={showRegisterApiKey ? "text" : "password"}
                    value={registerApiKey}
                    onChange={(e) => setRegisterApiKey(e.target.value)}
                    placeholder="APIキーを入力してください"
                    className="flex-1"
                    disabled={loading}
                  />
                  <Button
                    color="gray"
                    onClick={() => setShowRegisterApiKey(!showRegisterApiKey)}
                    disabled={loading}
                  >
                    {showRegisterApiKey ? (
                      <HiEyeOff className="w-4 h-4" />
                    ) : (
                      <HiEye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="line-token" value="LINE Channel Access Token" />
                <div className="flex gap-2">
                  <TextInput
                    id="line-token"
                    type={showChannelAccessToken ? "text" : "password"}
                    value={channelAccessToken}
                    onChange={(e) => setChannelAccessToken(e.target.value)}
                    placeholder="Channel Access Token"
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button
                    color="gray"
                    onClick={() =>
                      setShowChannelAccessToken(!showChannelAccessToken)
                    }
                    disabled={loading}
                  >
                    {showChannelAccessToken ? (
                      <HiEyeOff className="w-4 h-4" />
                    ) : (
                      <HiEye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  LINE Messaging APIのアクセストークン
                </p>
              </div>

              <div>
                <Label htmlFor="line-secret" value="LINE Channel Secret" />
                <div className="flex gap-2">
                  <TextInput
                    id="line-secret"
                    type={showChannelSecret ? "text" : "password"}
                    value={channelSecret}
                    onChange={(e) => setChannelSecret(e.target.value)}
                    placeholder="Channel Secret"
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button
                    color="gray"
                    onClick={() => setShowChannelSecret(!showChannelSecret)}
                    disabled={loading}
                  >
                    {showChannelSecret ? (
                      <HiEyeOff className="w-4 h-4" />
                    ) : (
                      <HiEye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  LINE Messaging APIのチャネルシークレット
                </p>
              </div>

              <Button onClick={handleSaveLINESettings} disabled={loading}>
                <HiSave className="mr-2 h-4 w-4" />
                {loading ? "保存中..." : "LINE設定を保存"}
              </Button>
            </div>
          </Card>

        </Tabs.Item>

        <Tabs.Item title="通知設定" icon={HiBell}>
          <Card>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">通知トリガー設定</h3>

              <div className="flex items-start">
                <Checkbox
                  id="notify-registration"
                  className="disabled:opacity-50"
                  checked={notificationConfig.notifyOnRegistration}
                  onChange={(e) =>
                    setNotificationConfig({
                      ...notificationConfig,
                      notifyOnRegistration: e.target.checked,
                    })
                  }
                  disabled={loading}
                />
                <Label htmlFor="notify-registration" className="ml-2">
                  <span className="font-medium">登録時に通知を送信</span>
                  <p className="text-sm text-gray-500 font-normal">
                    ゲストが呼び出し番号を登録した時にLINE通知を送信します
                  </p>
                </Label>
              </div>

              <div className="flex items-start">
                <Checkbox
                  id="notify-calling"
                  className="disabled:opacity-50"
                  checked={notificationConfig.notifyOnCalling}
                  onChange={(e) =>
                    setNotificationConfig({
                      ...notificationConfig,
                      notifyOnCalling: e.target.checked,
                    })
                  }
                  disabled={loading}
                />
                <Label htmlFor="notify-calling" className="ml-2">
                  <span className="font-medium">呼び出し時に通知を送信</span>
                  <p className="text-sm text-gray-500 font-normal">
                    ゲストの番号が呼び出された時にLINE通知を送信します
                  </p>
                </Label>
              </div>


              <hr className="my-4" />

              <h3 className="text-lg font-semibold">通知表示設定</h3>

              <div className="flex items-start mt-4">
                <Checkbox
                  id="hide-call-number"
                  className="disabled:opacity-50"
                  checked={notificationConfig.hideCallNumber}
                  onChange={(e) =>
                    setNotificationConfig({
                      ...notificationConfig,
                      hideCallNumber: e.target.checked,
                    })
                  }
                  disabled={loading}
                />
                <Label htmlFor="hide-call-number" className="ml-2">
                  <span className="font-medium">番号を表示しない</span>
                  <p className="text-sm text-gray-500 font-normal">
                    有効にすると、LINE通知から「番号」ラベルと大きな呼び出し番号表示が非表示になります
                  </p>
                </Label>
              </div>

              <div className="flex items-start mt-4">
                <Checkbox
                  id="send-image-with-notification"
                  className="disabled:opacity-50"
                  checked={notificationConfig.sendImageWithNotification}
                  onChange={(e) =>
                    setNotificationConfig({
                      ...notificationConfig,
                      sendImageWithNotification: e.target.checked,
                    })
                  }
                  disabled={loading}
                />
                <Label htmlFor="send-image-with-notification" className="ml-2">
                  <span className="font-medium">通知時に画像を送信する</span>
                  <p className="text-sm text-gray-500 font-normal">
                    呼び出し通知と同時に、解析元の待合スクリーン画像をLINEに送信します。誤通知の確認に役立ちますが、通信量が増加します。
                  </p>
                </Label>
              </div>

              <hr className="my-4" />

              <h3 className="text-lg font-semibold">曜日別通知時間帯設定</h3>
              <p className="text-sm text-gray-500 mb-4">
                曜日ごとに通知する時間帯を設定できます。「24時間スキップ」をONにすると、その曜日は終日通知を行わずスキップします。
              </p>

              <Table className="mt-4">
                <Table.Head>
                  <Table.HeadCell>曜日</Table.HeadCell>
                  <Table.HeadCell>有効</Table.HeadCell>
                  <Table.HeadCell>24時間スキップ</Table.HeadCell>
                  <Table.HeadCell>開始時刻</Table.HeadCell>
                  <Table.HeadCell>終了時刻</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {["日曜", "月曜", "火曜", "水曜", "木曜", "金曜", "土曜"].map(
                    (dayLabel, dayIndex) => (
                      <Table.Row
                        key={dayIndex}
                        className="bg-white dark:border-gray-700 dark:bg-gray-800"
                      >
                        <Table.Cell className="font-medium text-gray-900 dark:text-white">
                          {dayLabel}
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            checked={
                              notificationConfig.weekdayRestrictions?.[dayIndex]
                                ?.enabled || false
                            }
                            onChange={(e) => {
                              const newRestrictions = [
                                ...(notificationConfig.weekdayRestrictions ||
                                  []),
                              ];
                              newRestrictions[dayIndex] = {
                                ...newRestrictions[dayIndex],
                                enabled: e.target.checked,
                              };
                              setNotificationConfig({
                                ...notificationConfig,
                                weekdayRestrictions: newRestrictions,
                              });
                            }}
                            disabled={loading}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            checked={
                              notificationConfig.weekdayRestrictions?.[dayIndex]
                                ?.allDay || false
                            }
                            onChange={(e) => {
                              const newRestrictions = [
                                ...(notificationConfig.weekdayRestrictions ||
                                  []),
                              ];
                              newRestrictions[dayIndex] = {
                                ...newRestrictions[dayIndex],
                                allDay: e.target.checked,
                              };
                              setNotificationConfig({
                                ...notificationConfig,
                                weekdayRestrictions: newRestrictions,
                              });
                            }}
                            disabled={
                              loading ||
                              !notificationConfig.weekdayRestrictions?.[
                                dayIndex
                              ]?.enabled
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <TextInput
                            type="time"
                            value={
                              notificationConfig.weekdayRestrictions?.[dayIndex]
                                ?.startTime || "09:00"
                            }
                            onChange={(e) => {
                              const newRestrictions = [
                                ...(notificationConfig.weekdayRestrictions ||
                                  []),
                              ];
                              newRestrictions[dayIndex] = {
                                ...newRestrictions[dayIndex],
                                startTime: e.target.value,
                              };
                              setNotificationConfig({
                                ...notificationConfig,
                                weekdayRestrictions: newRestrictions,
                              });
                            }}
                            disabled={
                              loading ||
                              !notificationConfig.weekdayRestrictions?.[
                                dayIndex
                              ]?.enabled ||
                              notificationConfig.weekdayRestrictions?.[dayIndex]
                                ?.allDay
                            }
                            className="w-32"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <TextInput
                            type="time"
                            value={
                              notificationConfig.weekdayRestrictions?.[dayIndex]
                                ?.endTime || "18:00"
                            }
                            onChange={(e) => {
                              const newRestrictions = [
                                ...(notificationConfig.weekdayRestrictions ||
                                  []),
                              ];
                              newRestrictions[dayIndex] = {
                                ...newRestrictions[dayIndex],
                                endTime: e.target.value,
                              };
                              setNotificationConfig({
                                ...notificationConfig,
                                weekdayRestrictions: newRestrictions,
                              });
                            }}
                            disabled={
                              loading ||
                              !notificationConfig.weekdayRestrictions?.[
                                dayIndex
                              ]?.enabled ||
                              notificationConfig.weekdayRestrictions?.[dayIndex]
                                ?.allDay
                            }
                            className="w-32"
                          />
                        </Table.Cell>
                      </Table.Row>
                    ),
                  )}
                </Table.Body>
              </Table>

              <Button
                onClick={handleSaveNotificationSettings}
                disabled={loading}
                className="mt-4"
              >
                <HiSave className="mr-2 h-4 w-4" />
                {loading ? "保存中..." : "通知設定を保存"}
              </Button>
            </div>
          </Card>

        </Tabs.Item>

        <Tabs.Item title="通知テンプレート" icon={HiTemplate}>
          <Card>
            <div className="space-y-6">
              {/* 呼び出し通知 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  呼び出し通知
                </h3>

                <div>
                  <Label htmlFor="calling-title" value="呼び出し通知タイトル" />
                  <TextInput
                    id="calling-title"
                    type="text"
                    value={messageTemplates.callingTitle}
                    onChange={(e) =>
                      setMessageTemplates({
                        ...messageTemplates,
                        callingTitle: e.target.value,
                      })
                    }
                    placeholder="呼び出し通知（デフォルト）"
                    helperText="LINE Flex Messageのヘッダーに表示されるタイトル"
                    disabled={loading}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="calling-template"
                    value="呼び出し通知テンプレート"
                  />
                  <Textarea
                    id="calling-template"
                    rows={4}
                    value={messageTemplates.calling}
                    onChange={(e) =>
                      setMessageTemplates({
                        ...messageTemplates,
                        calling: e.target.value,
                      })
                    }
                    helperText="利用可能な変数: {{number}} - 番号, {{location}} - 場所"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="border-t pt-4"></div>

              {/* 登録通知 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  登録通知
                </h3>

                <div>
                  <Label
                    htmlFor="registration-title"
                    value="登録通知タイトル"
                  />
                  <TextInput
                    id="registration-title"
                    type="text"
                    value={messageTemplates.registrationTitle}
                    onChange={(e) =>
                      setMessageTemplates({
                        ...messageTemplates,
                        registrationTitle: e.target.value,
                      })
                    }
                    placeholder="登録しました（デフォルト）"
                    helperText="LINE Flex Messageのヘッダーに表示されるタイトル"
                    disabled={loading}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="registration-template"
                    value="登録通知テンプレート"
                  />
                  <Textarea
                    id="registration-template"
                    rows={3}
                    value={messageTemplates.registration}
                    onChange={(e) =>
                      setMessageTemplates({
                        ...messageTemplates,
                        registration: e.target.value,
                      })
                    }
                    helperText="利用可能な変数: {{number}} - 番号"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="border-t pt-4"></div>

              <Button onClick={handleSaveTemplates} disabled={loading}>
                <HiSave className="mr-2 h-4 w-4" />
                {loading ? "保存中..." : "テンプレートを保存"}
              </Button>
            </div>
          </Card>
        </Tabs.Item>

        <Tabs.Item title="IoTデバイス" icon={HiChip}>
          <APIKeyManager />
        </Tabs.Item>

        <Tabs.Item title="画像設定" icon={HiPhotograph}>
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              画像トリミング設定
            </h3>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="enableCrop"
                  checked={imageConfig.enableCrop}
                  onChange={(e) =>
                    setImageConfig((prev) => ({
                      ...prev,
                      enableCrop: e.target.checked,
                    }))
                  }
                  disabled={loading}
                />
                <Label htmlFor="enableCrop" value="トリミングを有効にする" />
              </div>

              {imageConfig.enableCrop && (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    画像上でドラッグして切り抜き領域を選択してください。
                  </p>
                  <CropSelector
                    imageUrl={latestImageUrl}
                    cropRegion={
                      imageConfig.cropRegion ?? { x: 0, y: 0, width: 0, height: 0 }
                    }
                    onChange={(region: CropRegion) =>
                      setImageConfig((prev) => ({
                        ...prev,
                        cropRegion: region,
                      }))
                    }
                  />
                </div>
              )}

              <Button
                color="blue"
                onClick={handleSaveImageSettings}
                disabled={loading}
              >
                <HiSave className="w-4 h-4 mr-2" />
                {loading ? "保存中..." : "画像設定を保存"}
              </Button>
            </div>
          </Card>
        </Tabs.Item>
      </Tabs>
    </div>
  );
});

export default SettingsPage;
